/**
 * Trợ lý AI dựng luồng: mô tả bằng lời → AI trả bản xem trước (tóm tắt + các bước)
 * → người dùng bấm Áp dụng. Không tự lưu: luồng chỉ vào canvas / được tạo ở trạng thái tắt.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, ArrowRight, Check, ExternalLink, HelpCircle, Loader2, RotateCcw, Send, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/misc'
import { apiError } from '@/lib/api-client'
import { i18n } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { actionMeta, DON_VI_CHO_LABEL, TOAN_TU_LABEL, triggerMeta } from '@/lib/automation/catalog'
import type { FlowConfig } from '@/hooks/use-automation'
import { useAiReadiness, useComposeFlow, type ComposeHistoryItem, type ComposeResult, type PlanRule, type PlanStep } from '@/hooks/use-automation-ai'

const viDu = () => [
  i18n.t('autoConfig:composer.example.newLead'),
  i18n.t('autoConfig:composer.example.monday'),
  i18n.t('autoConfig:composer.example.price'),
]

const tomRule = (r: PlanRule) => `${r.field} ${(TOAN_TU_LABEL[r.op] ?? r.op).toLowerCase()}${r.value ? ` “${r.value}”` : ''}`

function BuocXemTruoc({ steps, depth = 0 }: { steps: PlanStep[]; depth?: number }) {
  const { t } = useTranslation('autoConfig')
  return (
    <ol className={cn('space-y-1', depth > 0 && 'ml-4 border-l pl-2')}>
      {steps.map((s, i) => (
        <li key={i} className="text-xs">
          {s.kind === 'action' && (
            <span className="flex items-start gap-1"><ArrowRight className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
              <span><b>{actionMeta(s.actionType).ten}</b>{s.label && s.label !== actionMeta(s.actionType).ten ? ` — ${s.label}` : ''}
                {typeof s.config?.text === 'string' && s.config.text && <span className="mt-0.5 block rounded bg-muted/60 px-1.5 py-1 text-[11px] text-muted-foreground">{String(s.config.text).slice(0, 220)}</span>}
                {typeof s.config?.tag === 'string' && s.config.tag && <span className="text-muted-foreground"> “{s.config.tag}”</span>}
              </span></span>
          )}
          {s.kind === 'delay' && <span className="flex items-center gap-1"><ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground" /><span>{t('composer.preview.wait')} <b>{s.duration} {DON_VI_CHO_LABEL[s.unit] ?? s.unit}</b></span></span>}
          {s.kind === 'condition' && (
            <div>
              <span className="flex items-start gap-1"><ArrowRight className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" /><span>{t('composer.preview.if')} <b>{s.rules.map(tomRule).join(s.logic === 'or' ? ` ${t('composer.preview.or')} ` : ` ${t('composer.preview.and')} `)}</b></span></span>
              <p className="ml-4 mt-0.5 text-[10.5px] font-semibold uppercase text-emerald-600">{t('composer.preview.yes')}</p>
              {(s.yes?.length ?? 0) > 0 ? <BuocXemTruoc steps={s.yes!} depth={1} /> : <p className="ml-4 text-[11px] text-muted-foreground">{t('composer.preview.stop')}</p>}
              <p className="ml-4 mt-0.5 text-[10.5px] font-semibold uppercase text-rose-600">{t('composer.preview.no')}</p>
              {(s.no?.length ?? 0) > 0 ? <BuocXemTruoc steps={s.no!} depth={1} /> : <p className="ml-4 text-[11px] text-muted-foreground">{t('composer.preview.stop')}</p>}
            </div>
          )}
        </li>
      ))}
    </ol>
  )
}

export function KetQuaXemTruoc({ kq }: { kq: ComposeResult }) {
  const { t } = useTranslation('autoConfig')
  const tm = triggerMeta(kq.plan.trigger.type)
  return (
    <div className="space-y-2 rounded-md border bg-muted/20 p-2.5">
      {kq.summary && <p className="text-xs leading-relaxed">{kq.summary}</p>}
      <div className="rounded border bg-background p-2">
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t('composer.preview.when', { name: tm.ten })}</p>
        {kq.plan.trigger.onlyIf?.rules?.length ? <p className="mb-1 text-[11px] text-muted-foreground">{t('composer.preview.onlyIf', { rules: kq.plan.trigger.onlyIf.rules.map(tomRule).join(kq.plan.trigger.onlyIf.logic === 'or' ? ` ${t('composer.preview.or')} ` : ` ${t('composer.preview.and')} `) })}</p> : null}
        {kq.plan.steps.length ? <BuocXemTruoc steps={kq.plan.steps} /> : <p className="text-xs text-muted-foreground">{t('composer.preview.noSteps')}</p>}
      </div>
      {kq.questions.length > 0 && (
        <div className="rounded border border-sky-300/60 bg-sky-50 p-2 text-xs dark:bg-sky-950/30">
          <p className="mb-1 flex items-center gap-1 font-semibold"><HelpCircle className="h-3.5 w-3.5" /> {t('composer.preview.questions')}</p>
          <ul className="list-disc space-y-0.5 pl-4">{kq.questions.map((q, i) => <li key={i}>{q}</li>)}</ul>
        </div>
      )}
      {kq.warnings.length > 0 && (
        <div className="rounded border border-amber-300/60 bg-amber-50 p-2 text-xs dark:bg-amber-950/30">
          <p className="mb-1 flex items-center gap-1 font-semibold"><AlertTriangle className="h-3.5 w-3.5" /> {t('composer.preview.warnings')}</p>
          <ul className="list-disc space-y-0.5 pl-4">{kq.warnings.map((q, i) => <li key={i}>{q}</li>)}</ul>
        </div>
      )}
    </div>
  )
}

interface Props {
  /** Có = chế độ SỬA luồng hiện tại; không = tạo mới. */
  currentFlow?: () => FlowConfig | null
  applyLabel: string
  applying?: boolean
  /** 'dock' = khung ngang dưới canvas (xem trước bên trái, nhập bên phải); 'stack' = xếp dọc (dialog). */
  layout?: 'stack' | 'dock'
  onApply: (kq: ComposeResult) => void
  className?: string
}

export function AiComposerPanel({ currentFlow, applyLabel, applying, onApply, className, layout = 'stack' }: Props) {
  const { t } = useTranslation('autoConfig')
  const compose = useComposeFlow()
  const ai = useAiReadiness()
  const [prompt, setPrompt] = useState('')
  const [history, setHistory] = useState<ComposeHistoryItem[]>([])
  const [kq, setKq] = useState<ComposeResult | null>(null)
  const [loi, setLoi] = useState<string | null>(null)
  const [daAp, setDaAp] = useState(false)

  const gui = () => {
    const text = prompt.trim()
    if (!text || compose.isPending) return
    setLoi(null)
    // Có kết quả trước đó mà chưa áp dụng thì sửa tiếp trên chính bản đó; còn lại sửa luồng đang mở (nếu có).
    const cur = kq && !daAp ? kq.flowConfig : currentFlow?.() ?? null
    compose.mutate({ prompt: text, currentFlow: cur, history }, {
      onSuccess: (r) => {
        setKq(r); setDaAp(false); setPrompt('')
        setHistory((h): ComposeHistoryItem[] => [...h, { role: 'user' as const, text }, { role: 'assistant' as const, text: r.summary || 'Đã dựng luồng' /* i18n-ignore: gửi lại cho AI làm lịch sử hội thoại */ }].slice(-12))
      },
      onError: (e) => setLoi(apiError(e)),
    })
  }

  // Reset: xoá hội thoại + kết quả + lỗi, tải lại trạng thái kết nối AI.
  const reset = () => {
    compose.reset(); setPrompt(''); setHistory([]); setKq(null); setLoi(null); setDaAp(false); ai.refetch()
  }
  const dock = layout === 'dock'

  const tieuDe = (
    <div className="flex items-center justify-between">
      <p className="flex items-center gap-1.5 text-sm font-semibold"><Sparkles className="h-4 w-4 text-primary" /> {t('composer.title')}</p>
      <Button size="sm" variant="ghost" className="h-7 gap-1 px-2 text-xs" onClick={reset} disabled={compose.isPending || ai.fetching} title={t('composer.resetTitle')}>
        {ai.fetching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} Reset
      </Button>
    </div>
  )

  if (ai.state === 'loading') {
    return <div className={cn('flex flex-col gap-2', className)}>{tieuDe}<p className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('composer.checking')}</p></div>
  }
  if (ai.state !== 'ready') {
    return (
      <div className={cn('flex flex-col gap-2', className)}>
        {tieuDe}
        <div className="rounded-md border border-amber-300/60 bg-amber-50 p-3 text-xs dark:bg-amber-950/30">
          <p className="mb-1 flex items-center gap-1 font-semibold"><AlertTriangle className="h-3.5 w-3.5" /> {t('composer.needAi')}</p>
          <p className="mb-2 text-muted-foreground">{ai.reason}</p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" asChild><a href="/ai/nguon" target="_blank" rel="noopener noreferrer"><ExternalLink className="h-3.5 w-3.5" /> {t('composer.chooseAi')}</a></Button>
            <Button size="sm" variant="outline" onClick={reset} disabled={ai.fetching}>{ai.fetching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} {t('composer.reconnected')}</Button>
          </div>
        </div>
      </div>
    )
  }

  const xemTruoc = (
    <div className="min-h-0 space-y-2 overflow-y-auto">
      {!kq && !compose.isPending && (
        <div className="space-y-1.5">
          <p className="text-xs text-muted-foreground">{currentFlow?.() ? t('composer.describeEdit') : t('composer.describeNew')}</p>
          {viDu().map((v) => (
            <button key={v} type="button" onClick={() => setPrompt(v)} className="block w-full rounded-md border bg-card px-2 py-1.5 text-left text-xs hover:bg-accent/60">{v}</button>
          ))}
        </div>
      )}
      {compose.isPending && <p className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('composer.building')}</p>}
      {loi && <p className="rounded border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">{loi}</p>}
      {kq && !compose.isPending && (
        <>
          <KetQuaXemTruoc kq={kq} />
          {daAp
            ? <p className="flex items-center gap-1 text-xs text-emerald-600"><Check className="h-3.5 w-3.5" /> {currentFlow ? t('composer.appliedCanvas') : t('composer.created')}</p>
            : <Button size="sm" className="w-full" disabled={applying} onClick={() => { onApply(kq); setDaAp(true) }}>{applying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} {applyLabel}</Button>}
          <p className="text-[11px] text-muted-foreground">{t('composer.tweak')}</p>
        </>
      )}
    </div>
  )

  const oNhap = (
    <div className="space-y-1">
      <div className="flex items-end gap-1.5">
        <Textarea
          value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={dock ? 4 : 3} maxLength={2000} placeholder={t('composer.placeholder')}
          className="min-h-0 flex-1 resize-none text-xs"
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); gui() } }}
        />
        <Button size="icon" className="h-9 w-9 shrink-0" disabled={!prompt.trim() || compose.isPending} onClick={gui} title={t('composer.send')}><Send className="h-4 w-4" /></Button>
      </div>
      <p className="flex items-center gap-1 text-[10.5px] text-muted-foreground"><Sparkles className="h-3 w-3" /> {t('composer.disclaimer')}</p>
    </div>
  )

  if (dock) {
    return (
      <div className={cn('flex min-h-0 flex-col gap-2', className)}>
        {tieuDe}
        <div className="grid min-h-0 flex-1 gap-3 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          {xemTruoc}
          <div className="flex min-h-0 flex-col justify-end gap-2">{oNhap}</div>
        </div>
      </div>
    )
  }
  return (
    <div className={cn('flex min-h-0 flex-col gap-2', className)}>
      {tieuDe}
      <div className="min-h-0 flex-1 overflow-y-auto">{xemTruoc}</div>
      {oNhap}
    </div>
  )
}
